<template>
  <ToolInputsSidebar
    :loading="isRegenerating"
    :disabled="!canRegenerate"
    :messages="regenerationMessages"
    @regenerate="handleRegenerate"
  >
    <WebArticleInputsForm
      v-if="productKey === 'web_article'"
      :json-data="jsonData"
      @update:formData="regenerationForm = $event"
    />
    <PressReleaseInputsForm
      v-else-if="productKey === 'press_release'"
      :json-data="jsonData"
      @update:formData="regenerationForm = $event"
    />
    <DemGeneratorInputsForm
      v-else-if="productKey === 'dem_generator'"
      :json-data="jsonData"
      @update:formData="regenerationForm = $event"
    />
    <MicrocopySocialInputsForm
      v-else-if="productKey === 'microcopy_social'"
      :json-data="jsonData"
      @update:formData="regenerationForm = $event"
    />
  </ToolInputsSidebar>
</template>

<script>
import ToolInputsSidebar from '@/components/ToolInputsSidebar/ToolInputsSidebar.vue';
import WebArticleInputsForm from '@/pages/Tools/WebArticle/WebArticleInputsForm.vue';
import PressReleaseInputsForm from '@/pages/Tools/PressRelease/PressReleaseInputsForm.vue';
import DemGeneratorInputsForm from '@/pages/Tools/DemGenerator/DemGeneratorInputsForm.vue';
import MicrocopySocialInputsForm from '@/pages/Tools/MicrocopySocial/MicrocopySocialInputsForm.vue';

const TOOL_CONFIG = {
  web_article: {
    label: 'Article',
    canRegenerate(f) {
      return (
        f.title?.trim() &&
        f.seo_keywords?.length > 0 &&
        f.article_len &&
        f.target?.trim() &&
        f.customer_journey?.trim()
      );
    },
    buildParams(f) {
      return {};
    },
  },
  press_release: {
    label: 'Press Release',
    canRegenerate(f) {
      return (
        f.main_topic?.trim() &&
        f.article_kind?.trim()
      );
    },
    buildParams(f) {
      return {
        query_keywords: f.keywords || [],
      };
    },
  },
  dem_generator: {
    label: 'DEM',
    canRegenerate(f) {
      return (
        f.main_topic?.trim() &&
        f.target?.trim() &&
        f.length &&
        f.goal?.trim()
      );
    },
    buildParams(f) {
      return {};
    },
  },
  microcopy_social: {
    label: 'Micro Copy Social',
    canRegenerate(f) {
      return (
        f.main_topic?.trim() &&
        f.rubrica?.trim() &&
        f.communication_goal?.trim() &&
        f.social_media_channel?.length > 0 &&
        f.language?.trim()
      );
    },
    buildParams(f) {
      return {};
    },
  },
};

export default {
  name: 'TrentinoRegenerationSidebar',
  components: {
    ToolInputsSidebar,
    WebArticleInputsForm,
    PressReleaseInputsForm,
    DemGeneratorInputsForm,
    MicrocopySocialInputsForm,
  },
  props: {
    productKey: {
      type: String,
      required: true,
      validator: (v) => Object.keys(TOOL_CONFIG).includes(v),
    },
    jsonData: {
      type: Object,
      default: () => ({}),
    },
    orderDetailId: {
      type: [Number, String],
      required: true,
    },
    productKeys: {
      type: [Array, Object],
      default: () => [],
    },
  },
  data() {
    return {
      regenerationForm: null,
      isRegenerating: false,
      regenerationMessages: [],
    };
  },
  computed: {
    toolConfig() {
      return TOOL_CONFIG[this.productKey];
    },
    canRegenerate() {
      return this.regenerationForm && this.toolConfig.canRegenerate(this.regenerationForm);
    },
  },
  methods: {
    async handleRegenerate() {
      this.isRegenerating = true;
      this.regenerationMessages = [];

      const label = this.toolConfig.label;

      try {
        this.regenerationMessages.push(`Regenerating ${label}...`);

        const params = {
          ...this.regenerationForm,
          order_detail_id: this.orderDetailId,
          product_key: this.productKey,
          product_keys: this.productKeys,
          fast_api: true,
          ...this.toolConfig.buildParams(this.regenerationForm),
        };

        if (!params.brand_voice_id) {
          delete params.brand_voice_id;
        }

        const response = await this.$store.dispatch('createOrder', params);

        if (response) {
          this.regenerationMessages.push(`Regenerating ${label}... DONE!`);
        }

        this.$emit('regenerated', response);
      } catch (error) {
        this.regenerationMessages.push('Error during regeneration.');
        this.$emit('regeneration-error', error);
      } finally {
        this.isRegenerating = false;
        this.regenerationMessages = [];
      }
    },
  },
};
</script>
